import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import { useCookies } from 'react-cookie';
import { Row, Col, Button } from 'reactstrap';
import { DatePicker } from 'antd';
import Swal from 'sweetalert2';
import moment from 'moment';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    getDieselStockConsumptionStatement,
    getDieselStockConsumptionStatementCSV,
    getDieselStockConsumptionStatementPDF,
} from '../../services/ReportServices';
import { displayError, displayErrorToast } from '../../helpers';
import { openPdfResponse } from '../../utils/openPdfInApp';
import ReportExportDropdown from '../../components/ReportExportDropdown';
import ReportFilterToggleBar from '../../components/ReportFilterToggleBar';
import PremiumPageHeader from '../../components/PremiumPageHeader';
import FloatingControls from '../../components/FloatingControls';
import Sidebar from '../../Sidebar';
import '../../App.css';
import '../../button.css';

const { RangePicker } = DatePicker;

function formatQty(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
        return '';
    }
    return num.toFixed(3);
}

function DieselStockConsumptionStatement() {
    const location = useLocation();
    const isStandalone = location.pathname === '/DieselStockConsumptionStatement';
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);
    const [plants, setPlants] = useState([]);
    const [selectedPlantId, setSelectedPlantId] = useState('');
    const [rows, setRows] = useState([]);
    const [equipmentSummary, setEquipmentSummary] = useState([]);
    const [totals, setTotals] = useState({
        purchase_qty: 0,
        consumption_qty: 0,
        closing_stock: 0,
    });
    const [openingStock, setOpeningStock] = useState(0);
    const [filtersVisible, setFiltersVisible] = useState(true);
    const [meta, setMeta] = useState({ plant_alias: '', from_date: '', upto_date: '' });

    const initialFromDate = moment().startOf('month');
    const initialToDate = moment();
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);

    useEffect(() => {
        getUserAllowedPlants(cookies)
            .then((response) => {
                const options = (response.plant_list || [])
                    .filter((plant) => plant.status)
                    .map((plant) => ({
                        label: plant.plant_alias,
                        value: String(plant.id),
                    }))
                    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
                setPlants(options);
                if (options.length === 1) {
                    setSelectedPlantId(options[0].value);
                }
            })
            .catch((error) => {
                setPlants([]);
                displayErrorToast(error);
            });
    }, [cookies]);

    const handleDateChange = (values) => {
        if (values && values.length === 2) {
            setFromDate(values[0]);
            setToDate(values[1]);
        }
    };

    const constructFormData = () => ({
        plant_id: selectedPlantId,
        from_date: fromDate ? moment(fromDate).format('DD-MM-YYYY') : '',
        upto_date: toDate ? moment(toDate).format('DD-MM-YYYY') : '',
    });

    const applyStatement = (statement) => {
        setRows(statement?.rows || []);
        setEquipmentSummary(statement?.equipment_summary || []);
        setTotals(statement?.totals || {
            purchase_qty: 0,
            consumption_qty: 0,
            closing_stock: 0,
        });
        setOpeningStock(statement?.opening_stock || 0);
        setMeta({
            plant_alias: statement?.plant_alias || '',
            from_date: statement?.from_date || '',
            upto_date: statement?.upto_date || '',
        });
    };

    const handleGenerate = () => {
        const inputs = constructFormData();
        if (!inputs.plant_id || !inputs.from_date || !inputs.upto_date) {
            Swal.fire('Plant, from date and to date are required.', '', 'info');
            return;
        }
        setIsLoading(true);
        getDieselStockConsumptionStatement(cookies, inputs)
            .then((statement) => {
                applyStatement(statement);
                setFiltersVisible(false);
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                setRows([]);
                setEquipmentSummary([]);
                displayError(error.response?.data);
            });
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        handleGenerate();
    };

    const handleDownloadPdf = () => {
        const inputs = constructFormData();
        if (!inputs.plant_id || !inputs.from_date || !inputs.upto_date) {
            Swal.fire('Plant, from date and to date are required.', '', 'info');
            return;
        }
        setIsLoading(true);
        getDieselStockConsumptionStatementPDF(cookies, inputs)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Diesel Stock & Consumption Statement',
                    downloadFileName: 'diesel_stock_consumption_statement.pdf',
                });
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data);
            });
    };

    const handleDownloadCsv = () => {
        const inputs = constructFormData();
        if (!inputs.plant_id || !inputs.from_date || !inputs.upto_date) {
            Swal.fire('Plant, from date and to date are required.', '', 'info');
            return;
        }
        setIsLoading(true);
        getDieselStockConsumptionStatementCSV(cookies, inputs)
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'diesel_stock_consumption_statement.csv');
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data);
            });
    };

    const wrapHeader = { whiteSpace: 'normal', lineHeight: 1.25 };

    const columns = useMemo(() => [
        { fieldName: 'date', headerName: 'Date', size: 110, minSize: 100 },
        { fieldName: 'particulars', headerName: 'Particulars', size: 240, minSize: 160 },
        {
            fieldName: 'purchase_qty_display',
            headerName: 'Purchase (Ltr)',
            size: 140,
            minSize: 130,
            headerStyle: wrapHeader,
            getCellStyle: () => ({ textAlign: 'right' }),
        },
        {
            fieldName: 'diesel_available_display',
            headerName: 'Diesel Available (Ltr)',
            size: 160,
            minSize: 150,
            headerStyle: wrapHeader,
            getCellStyle: () => ({ textAlign: 'right' }),
        },
        {
            fieldName: 'equipment',
            headerName: 'Equipment / Vehicle',
            size: 180,
            minSize: 160,
            headerStyle: wrapHeader,
        },
        {
            fieldName: 'consumption_qty_display',
            headerName: 'Consumption (Ltr)',
            size: 150,
            minSize: 140,
            headerStyle: wrapHeader,
            getCellStyle: () => ({ textAlign: 'right' }),
        },
        {
            fieldName: 'closing_stock_display',
            headerName: 'Closing Stock (Ltr)',
            size: 160,
            minSize: 150,
            headerStyle: wrapHeader,
            getCellStyle: () => ({ textAlign: 'right' }),
        },
    ], []);

    const tableRows = useMemo(() => {
        const mapped = (rows || []).map((row, index) => ({
            ...row,
            id: row.id || `diesel-row-${index}`,
            purchase_qty_display: formatQty(row.purchase_qty),
            diesel_available_display: formatQty(row.diesel_available),
            consumption_qty_display: formatQty(row.consumption_qty),
            closing_stock_display: formatQty(row.closing_stock),
            hide_row_edit: 'Yes',
            hide_row_delete: 'Yes',
            hide_row_print: 'Yes',
        }));
        if (mapped.length) {
            mapped.push({
                id: 'totals-row',
                date: '',
                particulars: 'TOTAL',
                purchase_qty_display: formatQty(totals.purchase_qty),
                diesel_available_display: '',
                equipment: '',
                consumption_qty_display: formatQty(totals.consumption_qty),
                closing_stock_display: formatQty(totals.closing_stock),
                row_type: 'totals',
                hide_row_edit: 'Yes',
                hide_row_delete: 'Yes',
                hide_row_print: 'Yes',
                is_group_header: true,
            });
        }
        return mapped;
    }, [rows, totals]);

    const equipmentColumns = useMemo(() => [
        { fieldName: 'equipment_name', headerName: 'Equipment' },
        {
            fieldName: 'total_consumption_display',
            headerName: 'Total Diesel Consumed (Ltr)',
            getCellStyle: () => ({ textAlign: 'right' }),
        },
    ], []);

    const equipmentRows = useMemo(() => (
        (equipmentSummary || []).map((item, index) => ({
            ...item,
            id: `equip-${index}`,
            total_consumption_display: formatQty(item.total_consumption),
            hide_row_edit: 'Yes',
            hide_row_delete: 'Yes',
            hide_row_print: 'Yes',
        }))
    ), [equipmentSummary]);

    const pageContent = (
        <div className="report-embedded-page diesel-stock-statement-page">
            <div className="container">
                <div className="premium-page-header-wrap">
                    <PremiumPageHeader
                        module="STORE"
                        title="Diesel Stock & Consumption Statement"
                        subtitle="Track diesel opening stock, purchases, equipment issues, and closing stock for the selected plant and period."
                        modeLabel="REPORT"
                        headerActions={isStandalone ? (
                            <Link to="/ReportAll" className="premium-page-header-nav-tab" title="All Reports">
                                Reports
                            </Link>
                        ) : null}
                    />
                </div>
            </div>

            {!filtersVisible ? (
                <div className="container">
                    <ReportFilterToggleBar onShow={() => setFiltersVisible(true)} />
                </div>
            ) : null}

            {filtersVisible ? (
                <form className="form" onSubmit={handleSubmit}>
                    <div className="container">
                        <Row>
                            <Col xl={12} lg={12} md={12}>
                                <div className="report-legacy-filters form-row table-bordered shadow p-2 my-2 border-secondary form-control-panel">
                                    <div className="report-legacy-filters-fields">
                                        <select
                                            id="diesel_statement_plant_id"
                                            name="plant_id"
                                            className="form-control browser-default custom-select mandatory-form-control"
                                            required
                                            value={selectedPlantId}
                                            onChange={(event) => setSelectedPlantId(event.target.value)}
                                        >
                                            <option value="">Select plant</option>
                                            {plants.map((item) => (
                                                <option key={item.value} value={item.value}>
                                                    {item.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="report-legacy-filters-spacer" aria-hidden="true" />
                                        <div className="report-legacy-filters-spacer" aria-hidden="true" />
                                    </div>
                                    <div className="report-legacy-filters-row">
                                        <RangePicker
                                            value={[fromDate, toDate]}
                                            format="DD-MM-YYYY"
                                            onChange={handleDateChange}
                                            allowClear={false}
                                        />
                                        <div className="report-legacy-filters-spacer" aria-hidden="true" />
                                        <div className="report-legacy-actions">
                                            <ReportExportDropdown
                                                onCSV={handleDownloadCsv}
                                                onPDF={handleDownloadPdf}
                                                disabled={isLoading}
                                            />
                                            <Button
                                                color="primary"
                                                className="list-page-toolbar-generate-btn"
                                                type="submit"
                                                disabled={isLoading}
                                            >
                                                Generate
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </form>
            ) : null}

            <div className="container">
                {tableRows.length > 0 ? (
                    <div className="list-page-summary-bar">
                        <div className="list-page-summary-primary">
                            <span className="list-page-summary-label">DIESEL STOCK STATEMENT</span>
                            <span className="list-page-summary-count">
                                {meta.plant_alias
                                    ? `${meta.plant_alias} | ${meta.from_date} to ${meta.upto_date}`
                                    : `${meta.from_date} to ${meta.upto_date}`}
                            </span>
                        </div>
                        <div className="list-page-summary-metrics">
                            <div className="list-page-metric-card">
                                <span className="list-page-metric-label">Opening</span>
                                <span className="list-page-metric-value">{formatQty(openingStock)}</span>
                            </div>
                            <div className="list-page-metric-card">
                                <span className="list-page-metric-label">Purchase</span>
                                <span className="list-page-metric-value is-yellow">{formatQty(totals.purchase_qty)}</span>
                            </div>
                            <div className="list-page-metric-card">
                                <span className="list-page-metric-label">Consumption</span>
                                <span className="list-page-metric-value">{formatQty(totals.consumption_qty)}</span>
                            </div>
                            <div className="list-page-metric-card">
                                <span className="list-page-metric-label">Closing</span>
                                <span className="list-page-metric-value is-b2b">{formatQty(totals.closing_stock)}</span>
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="list-page-table-wrap">
                    <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title="Diesel Stock & Consumption Statement"
                                columns={columns}
                                data={tableRows}
                                exportFilename="diesel_stock_consumption_statement"
                                enableTableSearch={false}
                                enableExportToolbar={false}
                                isLoading={isLoading}
                                getRowSx={(row) => (
                                    row.row_type === 'opening' || row.row_type === 'totals'
                                        ? {
                                            backgroundColor: '#dbeafe !important',
                                            '& td': { fontWeight: 600 },
                                        }
                                        : {}
                                )}
                            />
                        </div>
                    </div>
                </div>

                {tableRows.length > 0 ? (
                    <p className="diesel-stock-formula-note">
                        Opening Stock ({formatQty(openingStock)}) + Purchases ({formatQty(totals.purchase_qty)})
                        {' '}− Consumption ({formatQty(totals.consumption_qty)})
                        {' '}= Closing Stock ({formatQty(totals.closing_stock)})
                    </p>
                ) : null}

                {equipmentRows.length > 0 ? (
                    <div className="diesel-stock-equipment-block">
                        <h5 className="diesel-stock-equipment-title">Equipment-wise Diesel Consumption Summary</h5>
                        <div className="list-page-table-wrap">
                            <div className="item-list-table-container">
                                <div className="table-responsive">
                                    <ReactTableWrapper
                                        title="Equipment-wise Summary"
                                        columns={equipmentColumns}
                                        data={equipmentRows}
                                        exportFilename="diesel_equipment_summary"
                                        enableTableSearch={false}
                                        enableExportToolbar={false}
                                        isLoading={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );

    if (!isStandalone) {
        return pageContent;
    }

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <FloatingControls showSave={false} homeLink="/Home" />
                {pageContent}
            </div>
        </div>
    );
}

export default DieselStockConsumptionStatement;
